import { ProgressIndicator } from 'components/2-molecules/ProgressIndicator';
import { Form } from 'components/3-organisms/Form/Form';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm, useFormState } from 'react-hook-form';
import { ConditionStep, ConditionStepProps } from './ConditionStep';
import { ResultStep, ResultStepProps } from './ResultStep';
import styles from './TravelDeduction.module.scss';
import { TravelDeductionNav, TravelDeductionNavAction, TravelDeductionNavProps } from './TravelDeductionNav';
import { TravelStep, TravelStepProps } from './TravelStep/TravelStep';
import { YearStep, YearStepProps } from './YearStep/YearStep';

export interface TravelDeductionProps extends YearStepProps, TravelStepProps, ConditionStepProps, ResultStepProps {
	steps: string[];
	actions: TravelDeductionNavProps['actions'];
	errorTexts: {
		submitFailed: string;
		numberOfErrors: string;
	};
	errorMessage: string;
	units: {
		days: string;
		kilometers: string;
	};
	processDataForStep?: (step: number) => void;
}

export type TravelDeductionStepState = {
	required?: boolean;
	hasError?: boolean;
};
export interface TravelDeductionFormData {
	dateStart: string;
	dateEnd: string;
	distance: string;
	workingDaysWithTransport: string;
	year: string;
	specialAddress: string;
	paymentBridgeUsage: string;
	paymentBridges: string[];
	transportType: string;
	paymentBridgeBothWays: string;
}

export const TravelDeduction = ({ steps, actions, errorTexts, processDataForStep, ...props }: TravelDeductionProps) => {
	const [currentStep, setCurrentStep] = useState(null);
	const [errorSummary, setErrorSummary] = useState(0);
	const [formData, setFormData] = useState<TravelDeductionFormData>(null);

	const { register, handleSubmit, control, watch, setValue } = useForm<TravelDeductionFormData>();

	const { errors } = useFormState({
		control,
	});

	const watchPaymentBridgeUsage = watch('paymentBridgeUsage');
	const watchPaymentBridges = watch('paymentBridges');

	const handleStep = (action: TravelDeductionNavAction) => {
		switch (action) {
			case 'next':
				return setCurrentStep(currentStep + 1);
			case 'back':
				return setCurrentStep(currentStep - 1);
			case 'restart':
				return setCurrentStep(1);
		}
	};
	useEffect(() => {
		if (!currentStep) return setCurrentStep(1);
		processDataForStep?.(currentStep);
	}, [currentStep, processDataForStep]);

	const onSubmit: SubmitHandler<TravelDeductionFormData> = (data) => {
		handleStep('next');
		setFormData(data);
		setErrorSummary(0);
	};

	const onError = (data) => {
		const totalErrors = Object.keys(data).length;
		setErrorSummary(totalErrors);
	};

	return (
		<div className={styles.TravelDeduction}>
			<div className={styles.TravelDeduction_wrapper}>
				<ProgressIndicator className={styles.TravelDeduction_progress} steps={steps} activeStep={currentStep - 1} />
				<Form onSubmit={handleSubmit(onSubmit, onError)} errorTexts={errorTexts} errorSummary={errorSummary}>
					{currentStep === 1 ? (
						<YearStep {...props} />
					) : currentStep === 2 ? (
						<TravelStep {...props} register={register} setValue={setValue} control={control} errors={errors} savedData={formData} />
					) : currentStep === 3 ? (
						<ConditionStep
							{...props}
							savedData={formData}
							showPaymentBridges={watchPaymentBridgeUsage === 'true'}
							showTransportTypes={watchPaymentBridges?.length > 0}
							register={register}
						/>
					) : currentStep === 4 ? (
						<ResultStep {...props} formData={formData} />
					) : null}
					<TravelDeductionNav actions={actions} currentStep={currentStep} totalSteps={steps?.length} navActionCallBack={handleStep} />
				</Form>
			</div>
		</div>
	);
};
